import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { DocusealForm } from '@docuseal/react';
import { BackLink, ErrorNotice, LoadingState, PageHeading } from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import { contractPaths, subjectFromParams } from '@/lib/contracts';
import { readContract } from '@/lib/contracts';

/** The BCE/Provider countersigner role (matches the API's PROVIDER_ROLE). */
const PROVIDER_ROLE = 'BCE';

/**
 * BCE countersignature page: after the client signs, an admin signs on
 * behalf of Best Corporate Events (Salesforce's Provider `_2` signer). The
 * embedded form is the BCE submitter's signing room.
 *
 * @returns The countersign page.
 */
export function ContractCountersignPage(): React.ReactElement {
  const { eventId, campaignId, requestId, organizationId, contractId } = useParams<{ eventId?: string; campaignId?: string; requestId?: string; organizationId?: string; contractId: string }>();
  const paths = contractPaths(subjectFromParams({ eventId, campaignId, requestId, organizationId }));

  const contract = useQuery({
    queryKey: ['contract', contractId],
    queryFn: () => readContract(contractId ?? ''),
    enabled: contractId !== undefined,
  });

  const bce = contract.data?.submitters.find((s) => s.role === PROVIDER_ROLE);
  const client = contract.data?.submitters.find((s) => s.role !== PROVIDER_ROLE);

  return (
    <section className="animate-portal-rise">
      <BackLink to={paths.back}>Back to contracts</BackLink>

      <div className="mt-3">
        <PageHeading
          eyebrow="COUNTERSIGN — BEST CORPORATE EVENTS"
          size="compact"
          title={contract.data?.name ?? 'Contract'}
        />
      </div>

      <div className="mt-4">
        {contract.isPending ? <LoadingState label="Loading…" /> : null}
        {contract.isError ? (
          <ErrorNotice title="Contract unavailable" message={errorMessage(contract.error)} />
        ) : null}

        {contract.data && !bce?.embedSrc ? (
          <ErrorNotice
            title="Not ready to countersign"
            message="Publish this contract first — the BCE signature is created when it goes to the client."
          />
        ) : null}

        {contract.data && bce?.embedSrc && client && !client.completedAt ? (
          <ErrorNotice
            title="Waiting on the client"
            message="The client signs first; you can countersign once their signature is recorded."
          />
        ) : null}

        {bce?.embedSrc && client?.completedAt && !bce.completedAt ? (
          <div className="overflow-hidden rounded-portal-button border bg-white">
            <DocusealForm src={bce.embedSrc} expand={false} />
          </div>
        ) : null}

        {bce?.completedAt ? (
          <p className="text-portal-small text-portal-muted">
            Countersigned {new Date(bce.completedAt).toLocaleString()}.
          </p>
        ) : null}
      </div>
    </section>
  );
}
